import { useMemo } from 'react';
import type { ReactElement } from 'react';

import { useTranslation } from 'react-i18next';

import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import StraightenRoundedIcon from '@mui/icons-material/StraightenRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Box, Tab, Tabs } from '@mui/material';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';

import {
  useAttributeGroups,
  useAttributes,
  useMeasurementFamilies,
  useProductFamilies,
} from '../hooks';
import { useCatalogPermissions } from '../lib/useCatalogPermissions';

/** Workspace root — also the "Templates" tab, so it is matched last (every sibling is a prefix of it). */
const CATALOG_STRUCTURE_ROOT = '/catalog/templates';

interface StructureTab {
  key: string;
  /** Absolute route the tab links to. */
  to: string;
  /** `catalog.json` key for the visible label — deliberately the *page's own* title key, so the tab
   *  and the `h1` it leads to can never drift apart. */
  labelKey: string;
  /** `ReactElement`, not `ReactNode`: MUI's `Tab.icon` does not accept `null`/`undefined` members. */
  icon: ReactElement;
}

/**
 * The catalog data model, top-down: a **template** says which attributes a product has, attributes are
 * shown in **groups**, and a MEASUREMENT attribute picks its **units**. The tab order mirrors that
 * chain so the workspace reads as one model rather than four unrelated dictionaries.
 */
const STRUCTURE_TABS: StructureTab[] = [
  {
    key: 'templates',
    to: CATALOG_STRUCTURE_ROOT,
    labelKey: 'productFamilies.title',
    icon: <AccountTreeRoundedIcon />,
  },
  {
    key: 'attribute-groups',
    to: `${CATALOG_STRUCTURE_ROOT}/attribute-groups`,
    labelKey: 'attributeGroups.title',
    icon: <FolderRoundedIcon />,
  },
  {
    key: 'attributes',
    to: `${CATALOG_STRUCTURE_ROOT}/attributes`,
    labelKey: 'attributes.title',
    icon: <TuneRoundedIcon />,
  },
  {
    key: 'units',
    to: `${CATALOG_STRUCTURE_ROOT}/units`,
    labelKey: 'measurementFamilies.title',
    icon: <StraightenRoundedIcon />,
  },
];

/**
 * Resolves the active tab from the current path.
 *
 * The workspace root doubles as the first tab, so it is a prefix of every sibling — matching it first
 * would light up "Templates" on every sub-route. Non-root tabs are therefore tested first, and the root
 * is the fallback.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function activeStructureTabIndex(pathname: string): number {
  const index = STRUCTURE_TABS.findIndex(
    (tab) =>
      tab.to !== CATALOG_STRUCTURE_ROOT &&
      (pathname === tab.to || pathname.startsWith(`${tab.to}/`)),
  );
  return index === -1 ? 0 : index;
}

/** Small count pill rendered after a tab label. */
function TabCount({ value }: { value: number }) {
  return (
    <Box
      component="span"
      sx={{
        ml: 0.75,
        px: 0.75,
        minWidth: 20,
        borderRadius: 999,
        bgcolor: 'action.hover',
        color: 'text.secondary',
        fontSize: 12,
        fontWeight: 700,
        lineHeight: '18px',
        textAlign: 'center',
      }}
    >
      {value}
    </Box>
  );
}

/**
 * Catalog **data-model workspace** — the single sidebar entry ("Templates") behind which the four
 * catalog-structure dictionaries live: templates, attribute groups, attributes and units of measure.
 *
 * <p><strong>Why one entry instead of four.</strong> The sidebar's Catalog section had grown to five
 * items plus a stray "Measurements" parked under Logistics, which read as six unrelated screens even
 * though they are one model: a template is a set of attributes, attributes are shown in groups, and a
 * MEASUREMENT attribute picks its units. Merchants work in the model rarely and in bursts (setting the
 * catalog up, or adding a field), so the four dictionaries earn one entry between them — not four
 * permanent lines competing with Products and Categories, which are used daily.
 *
 * <p>The tab strip is rendered <em>here</em> rather than inside each page on purpose: the pages stay
 * standalone (they are still routable and unit-testable on their own), and mounting the four list
 * queries once at this level means switching tabs is served from the TanStack cache instead of
 * refetching — the counts on the tabs are the same data, so they cost nothing extra.
 */
export function CatalogStructureLayout() {
  const { t } = useTranslation('catalog');
  const { canStructureView } = useCatalogPermissions();
  const { pathname } = useLocation();

  const familiesQuery = useProductFamilies({ enabled: canStructureView });
  const groupsQuery = useAttributeGroups({ enabled: canStructureView });
  const attributesQuery = useAttributes({ enabled: canStructureView });
  const measurementsQuery = useMeasurementFamilies({ enabled: canStructureView });

  const counts = useMemo<Record<string, number | undefined>>(
    () => ({
      templates: familiesQuery.data?.length,
      'attribute-groups': groupsQuery.data?.length,
      attributes: attributesQuery.data?.length,
      units: measurementsQuery.data?.length,
    }),
    [attributesQuery.data, familiesQuery.data, groupsQuery.data, measurementsQuery.data],
  );

  // A user without `catalog.structure.view` gets no switcher — each page below already renders the
  // shared "Access restricted" state, and a strip of tabs they cannot open is noise, not navigation.
  if (!canStructureView) {
    return <Outlet />;
  }

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.default',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ width: '100%', mx: 'auto', maxWidth: 1200, px: { xs: 2, md: 3 } }}>
          <Tabs
            value={activeStructureTabIndex(pathname)}
            aria-label={t('structure.tabs.ariaLabel')}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ minHeight: 52 }}
          >
            {STRUCTURE_TABS.map((tab) => {
              const count = counts[tab.key];
              return (
                <Tab
                  key={tab.key}
                  component={RouterLink}
                  to={tab.to}
                  icon={tab.icon}
                  iconPosition="start"
                  label={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                      {t(tab.labelKey)}
                      {typeof count === 'number' ? <TabCount value={count} /> : null}
                    </Box>
                  }
                  sx={{ minHeight: 52, textTransform: 'none' }}
                />
              );
            })}
          </Tabs>
        </Box>
      </Box>

      <Outlet />
    </>
  );
}
